/**
 * Calculus Kingdom, surveyed: the pass, and the inside of all four of its
 * regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **One road, and everything else is off it up or down.** The kingdom's own
 * region names are already vertical — a gate, heights, depths, a realm past
 * both — and the mathematics is why: the derivative and the integral are the
 * same road walked in opposite directions, so the heights and the depths are
 * the same climb with the sign changed, and the road between them runs through
 * a gap in the wall rather than round it. You come in at the gate because a
 * limit is what the whole subject is built on and there is no way past it.
 *
 * On the continent this kingdom sits a long way north, because it is a long way
 * past the arithmetic. Inside it, the far shelf carries the Infinity Realm and
 * is reached last: a sum with no last term is what both halves of the calculus
 * turn out to lean on.
 *
 * **Terrain is scenery and never a lock.** This is a kingdom of rock, and rock
 * refuses a step — the easiest terrain in the legend to strand a region behind.
 * `validateTown` and `validateRegionMap` walk all five from their arrival tiles
 * on every `npm run content:validate`, and every concept below is an ordinary
 * card on the kingdom page besides.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-calculus';

/* =============================================================== the pass === */

/**
 * The kingdom: the road along the pass, the heights above, the chasm below.
 *
 * The wall down the middle separates the country you climb from the country you
 * descend into, and the road goes through it at one point — which is the whole
 * geography, because the two halves are not two subjects. The gate is south of
 * the road on the way in; the far shelf, east of the wall and above the chasm,
 * is the realm of the infinite sums.
 */
const CALCULUS_PASS: Town = {
  kingdomId: KINGDOM,
  caption: 'One road through the pass: the heights climbed on one side, the chasm descended on the other, and a gap in the wall between them.',
  width: 26,
  height: 18,
  /* On the road at the head of the pass, the wall in front of you. */
  start: { x: 8, y: 8 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    '^^..........^^..........^^',
    '^^..........^^..........^^',
    '^^..........^^..........^^',
    '^^..........^^..........^^',
    '^^..........^^..........^^',
    '^^...#......^^......#...^^',
    '^....#......^^......#....^',
    '^########################^',
    '^.....#..#..^^....#......^',
    '^,,,,,#,,,,^^^,,,,#,,,,,^^',
    '^,,,,,#,,,,^^^,,,,#,,,,,^^',
    '^,,,,,#,,,,^^^,,,,#,,,,,^^',
    '^,,,,,,,,,,^^^,,,,,,,,,,^^',
    '^,,,,,,,,,,^^^,,,,,,,,,,^^',
    '^,,,,,,,,,,^^^,,,,,,,,,,^^',
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
  ],
  places: [
    { kind: 'road', name: 'The Pass Road', emoji: '🗺️', x: 1, y: 9 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 3, y: 10 },
    { kind: 'region', regionId: 'region-limit-gate', x: 6, y: 13 },
    { kind: 'region', regionId: 'region-derivative-heights', x: 5, y: 3 },
    { kind: 'region', regionId: 'region-integral-depths', x: 18, y: 13 },
    { kind: 'region', regionId: 'region-infinity-realm', x: 20, y: 4 },
    { kind: 'boss', bossId: 'boss-calculus-kingdom', x: 9, y: 10 },
  ],
};

/* =========================================================== the regions === */

/**
 * Limit Gate — two towers with a road between them, and no gate across it.
 *
 * You approach a limit; you do not arrive at one. So there are two gatehouses
 * either side of the road and nothing barring it, and the rate of change stands
 * further up the road past both — which is what a limit was wanted for in the
 * first place.
 */
const LIMIT_GATE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-limit-gate',
  caption: 'Two gatehouses either side of the road, and nothing at all barring the road between them.',
  width: 17,
  height: 12,
  /* On the road below the gatehouses, looking up through them. */
  start: { x: 8, y: 8 },
  rows: [
    '^^^^^^^^^^^^^^^^^',
    '^......###......^',
    '^......###......^',
    '^......###......^',
    '^...%%%###%%%...^',
    '^...%__###__%...^',
    '^...%%%###%%%...^',
    '^......###......^',
    '^......###......^',
    '^......###......^',
    '^......###......^',
    '^^^^^^^^#^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'limits', x: 5, y: 5 },
    { kind: 'concept', conceptId: 'continuity', x: 11, y: 5 },
    { kind: 'concept', conceptId: 'rates-of-change', x: 8, y: 2 },
    { kind: 'road', name: 'The Gate Road', emoji: '🌌', x: 8, y: 11 },
  ],
};

/**
 * Derivative Heights — four ledges, and every way between them is a slope.
 *
 * There is no flat route up: each ledge is joined to the next by a pitch at one
 * end or the other, so crossing this region is a sequence of gradients. The
 * derivative itself is on the top ledge; the rules are beside it; and the
 * things you do with a derivative once you have one — shape, optimisation,
 * motion — are on the ledges below, which is the order they are learned in.
 */
const DERIVATIVE_HEIGHTS: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-derivative-heights',
  caption: 'Four ledges up the face, and no way between any two of them that is not a slope.',
  width: 19,
  height: 13,
  /* On the bottom ledge, at the head of the first pitch. */
  start: { x: 9, y: 11 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^',
    '^^^^^^^^^^^^^^^^^^^',
    '^.................^',
    '^...#.........#...^',
    '^^^^#^^^^^^^^^#^^^^',
    '^...#.........#...^',
    '^...#.........#...^',
    '^^^^#^^^^^^^^^#^^^^',
    '^...#.........#...^',
    '^........#........^',
    '^^^^^^^^^#^^^^^^^^^',
    '^........#........^',
    '^^^^^^^^^#^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'derivatives', x: 9, y: 2 },
    { kind: 'concept', conceptId: 'derivative-rules', x: 3, y: 2 },
    { kind: 'concept', conceptId: 'implicit-differentiation', x: 16, y: 2 },
    { kind: 'concept', conceptId: 'derivative-applications', x: 3, y: 5 },
    { kind: 'concept', conceptId: 'optimization', x: 16, y: 5 },
    { kind: 'concept', conceptId: 'motion-derivatives', x: 9, y: 8 },
    { kind: 'road', name: 'The Ledge Path', emoji: '🌌', x: 9, y: 12 },
  ],
};

/**
 * Integral Depths — four galleries down, the largest region in the world.
 *
 * Fifteen concepts, which is more than any other region holds and far too many
 * for a row, so they are cut as galleries one under the next: what an integral
 * *is* at the top, the five ways of actually doing one on the second, what you
 * measure with them on the third, and the two that change coordinates
 * altogether at the bottom. Each gallery is reached by a shaft at one end or
 * the other, so going deeper is always a deliberate turn.
 */
const INTEGRAL_DEPTHS: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-integral-depths',
  caption: 'Four galleries cut one under the next: what an integral is, the ways of doing one, what they measure, and the two that change the coordinates.',
  width: 20,
  height: 15,
  /* On the bottom gallery, at the foot of the last shaft. */
  start: { x: 10, y: 12 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^',
    '^__________________^',
    '^__#____________#__^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^__#____________#__^',
    '^__________________^',
    '^__#____________#__^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^__#____________#__^',
    '^__________________^',
    '^__#____________#__^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^__#____________#__^',
    '^_________#________^',
    '^^^^^^^^^^#^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'antiderivatives', x: 6, y: 1 },
    { kind: 'concept', conceptId: 'integrals', x: 10, y: 1 },
    { kind: 'concept', conceptId: 'fundamental-theorem', x: 13, y: 1 },
    { kind: 'concept', conceptId: 'partial-fractions', x: 6, y: 4 },
    { kind: 'concept', conceptId: 'trigonometric-substitution', x: 14, y: 4 },
    { kind: 'concept', conceptId: 'u-substitution', x: 5, y: 5 },
    { kind: 'concept', conceptId: 'integration-by-parts', x: 8, y: 5 },
    { kind: 'concept', conceptId: 'trigonometric-integrals', x: 11, y: 5 },
    { kind: 'concept', conceptId: 'center-of-mass', x: 6, y: 8 },
    { kind: 'concept', conceptId: 'work-integrals', x: 14, y: 8 },
    { kind: 'concept', conceptId: 'improper-integrals', x: 5, y: 9 },
    { kind: 'concept', conceptId: 'arc-length', x: 8, y: 9 },
    { kind: 'concept', conceptId: 'surface-of-revolution', x: 11, y: 9 },
    { kind: 'concept', conceptId: 'parametric-curves', x: 7, y: 13 },
    { kind: 'concept', conceptId: 'polar-calculus', x: 13, y: 13 },
    { kind: 'road', name: 'The Shaft Head', emoji: '🌌', x: 10, y: 14 },
  ],
};

/**
 * Infinity Realm — a road with no end drawn on it, and the tests beside it.
 *
 * The five tests stand in a row north of the road because that is what they
 * are: five things to try, in an order, on a sum you cannot add up. The three
 * south of it are what you get once a series converges — and the road itself
 * runs the full width of the map and off both edges, because the one thing this
 * region is about is a sum with no last term.
 */
const INFINITY_REALM: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-infinity-realm',
  caption: 'A road running off both edges of the map, the five tests standing along the north side of it, and what survives them on the south.',
  width: 19,
  height: 13,
  /* On the long road, between the tests and what follows them. */
  start: { x: 8, y: 7 },
  rows: [
    'fffffffffffffffffff',
    'f.................f',
    'f.................f',
    'f.................f',
    'f..#..#..#..#..#..f',
    'f..#..#..#..#..#..f',
    'f#################f',
    'f....#....#...#...f',
    'f....#....#...#...f',
    'f....#....#...#...f',
    'f........#........f',
    'f........#........f',
    'fffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'sequence-limits', x: 3, y: 3 },
    { kind: 'concept', conceptId: 'series-convergence', x: 6, y: 3 },
    { kind: 'concept', conceptId: 'integral-test', x: 9, y: 3 },
    { kind: 'concept', conceptId: 'comparison-tests', x: 12, y: 3 },
    { kind: 'concept', conceptId: 'ratio-test', x: 15, y: 3 },
    { kind: 'concept', conceptId: 'alternating-series', x: 5, y: 9 },
    { kind: 'concept', conceptId: 'power-series', x: 10, y: 9 },
    { kind: 'concept', conceptId: 'taylor-series', x: 14, y: 9 },
    { kind: 'road', name: 'The Long Road', emoji: '🌌', x: 9, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */

export const CALCULUS_TOWN: Town = CALCULUS_PASS;

export const CALCULUS_REGION_MAPS: readonly RegionMap[] = [
  LIMIT_GATE,
  DERIVATIVE_HEIGHTS,
  INTEGRAL_DEPTHS,
  INFINITY_REALM,
];
